import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
    Button,
    Avatar,
    Skeleton,
    Grid,
    Card,
    CardContent,
    Tooltip,
    tooltipClasses,
    styled,
    CardMedia,
    useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import {
    FirstPage,
    LastPage,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    VerifiedUser,
    ErrorOutline,
    ShowChart,
    BarChart,
    BubbleChart,
    Link as LinkIcon,
    CheckCircleOutline,
    HighlightOff,
    UnfoldMore,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { transformToBTC } from '../utils/helpers';
import { useBitcoinPrice } from '../state/context/bitcoin.context';


// // --- HELPER FUNCTIONS ---
// const transformToBTC = (sats) => {
//     if (sats === null || sats === undefined || isNaN(sats)) return 'N/A';
//     const btc = sats / 1e11;
//     if (btc === 0) return '0.00 BTC';
//     if (btc < 0.01) return `${ btc.toFixed(4) } BTC`;
//     return `${ btc.toLocaleString(undefined, {
//         minimumFractionDigits: 2,
//         maximumFractionDigits: 2,
//     }) } BTC`;
// };

const timeAgo = (dateString, t) => {
    if (!dateString) return 'N/A';

    // Handle different date formats
    let date;
    if (typeof dateString === 'number') {
        // Unix timestamp (seconds)
        date = new Date(dateString * 1000);
    } else if (typeof dateString === 'string') {
        // ISO string or other string format
        date = new Date(dateString);
    } else {
        return 'N/A';
    }

    // Check if date is valid
    if (isNaN(date.getTime())) return 'N/A';

    const now = new Date();
    const seconds = Math.round((now - date) / 1000);

    // Allow for small negative values (clock skew)
    if (isNaN(seconds) || seconds < -60) return 'N/A';

    const absSeconds = Math.abs(seconds);
    const minutes = Math.round(absSeconds / 60);

    if (minutes < 1) {
        return t('time.justNow');
    }
    if (minutes === 1) {
        return t('time.aMin');
    }
    if (minutes < 60) {
        const translated = t('time.mins', { count: minutes });
        return translated.includes('{count}') ? translated.replace('{count}', minutes) : translated;
    }

    const hours = Math.round(minutes / 60);
    if (hours === 1) {
        return t('time.anHour');
    }
    if (hours < 24) {
        const translated = t('time.hours', { count: hours });
        return translated.includes('{count}') ? translated.replace('{count}', hours) : translated;
    }

    const days = Math.round(hours / 24);
    if (days === 1) {
        return t('time.aDay');
    }
    if (days < 30) {
        const translated = t('time.days', { count: days });
        return translated.includes('{count}') ? translated.replace('{count}', days) : translated;
    }

    const months = Math.round(days / 30);
    if (months === 1) {
        return t('time.aMonth');
    }
    if (months < 12) {
        const translated = t('time.months', { count: months });
        return translated.includes('{count}') ? translated.replace('{count}', months) : translated;
    }

    const years = Math.round(months / 12);
    if (years === 1) {
        return t('time.aYear');
    }
    const translated = t('time.years', { count: years });
    return translated.includes('{count}') ? translated.replace('{count}', years) : translated;
};

const abbreviateString = (str, startChars = 6, endChars = 4) => {
    if (!str || str.length <= startChars + endChars) {
        return str;
    }
    return `${ str.substring(0, startChars) }...${ str.substring(str.length - endChars) }`;
};

const formatBTCWithUSD = (sats, btcPrice) => {
    if (sats === null || sats === undefined || isNaN(sats)) return 'N/A';

    const btcValue = transformToBTC(sats);

    if (!btcPrice || btcValue === 'N/A') {
        return btcValue;
    }

    // Extract numeric value from BTC string
    const btcNumeric = parseFloat(btcValue.replace(/[^\d.-]/g, ''));
    const isSats = btcValue.includes('SATS');

    let usdValue;
    if (isSats) {
        // Convert SATS to BTC then to USD
        usdValue = (btcNumeric / 100000000) * btcPrice;
    } else {
        // Already in BTC
        usdValue = btcNumeric * btcPrice;
    }

    if (usdValue < 0.01) {
        return btcValue; // Don't show USD for very small amounts
    }

    const formattedUSD = usdValue.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'inherit' }}>
            <Typography variant="body2" sx={{ fontFamily: 'inherit', lineHeight: 1.2 }}>
                {btcValue}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', lineHeight: 1 }}>
                {formattedUSD}
            </Typography>
        </Box>
    );
};

// --- DATA & SORTING UTILITIES ---
const DEFAULT_ITEMS_PER_PAGE = 20;

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

// --- STYLED COMPONENTS ---
const DetailedTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${ tooltipClasses.tooltip }`]: {
        backgroundColor: theme.palette.mode === 'light'
            ? 'rgba(255, 255, 255, 0.95)'
            : 'rgba(25, 26, 28, 0.9)',
        color: theme.palette.text.primary,
        maxWidth: 600,
        fontSize: theme.typography.pxToRem(12),
        border: `1px solid ${ theme.palette.divider }`,
        backdropFilter: 'blur(10px)',
        padding: '16px',
    },
}));

// --- FEATURED TOKEN CAROUSEL COMPONENTS ---
const TrendingCard = ({ collection }) => {
    if (!collection) return null;
    const navigate = useNavigate();
    const theme = useTheme();
    const { t } = useTranslation();
    const { price: btcPrice } = useBitcoinPrice();

    return (
        <Card
            sx={{
                minWidth: 250,
                mr: 2,
                boxShadow: 3,
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                flexShrink: 0,
                height: 300,
                borderRadius: 2,
                '&:hover': {
                    boxShadow: 6,
                    transform: 'scale(1.03)',
                    transition: 'transform 0.3s ease-in-out',
                },
            }}
            onClick={() => window.open(`/tokens/${ collection.id }`)}
        >
            <CardMedia
                component="img"
                image={`https://images.odin.fun/token/${ collection.id }`}
                alt={collection.name}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    zIndex: 1,
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 2,
                    background:
                        'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,.7) 60%, rgba(0,0,0,1) 100%)',
                }}
            />
            <CardContent sx={{ position: 'relative', zIndex: 3, color: 'white', p: 2 }}>
                <Typography variant="h6" component="div" sx={{ fontSize: '1rem', mb: 0.5, fontWeight: 'bold' }}>
                    {collection.name}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem' }}>
                            {t('tokenExplore.floor')}:
                        </Typography>
                        <Box sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.7rem' }}>
                            {formatBTCWithUSD(collection.price, btcPrice)}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'right' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem' }}>
                            {t('tokenExplore.mc')}:
                        </Typography>
                        <Box sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.7rem' }}>
                            {formatBTCWithUSD(collection.marketcap, btcPrice)}
                        </Box>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const TrendingTokens = () => {
    const [trendingData, setTrendingData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const scrollRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);
    const requestRef = useRef();
    const { t } = useTranslation();

    useEffect(() => {
        const fetchTrending = async () => {
            setLoading(true);
            try {
                const response = await fetch(`https://api.odin.fun/v1/tokens?limit=10&sort=marketcap:desc`);
                if (!response.ok) throw new Error('Failed to fetch trending tokens');
                const result = await response.json();
                setTrendingData(result.data || []);
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        };
        fetchTrending();
    }, []);

    const loopedData = [...trendingData, ...trendingData];

    useEffect(() => {
        const scroll = () => {
            if (scrollRef.current && !isHovered) {
                scrollRef.current.scrollLeft += 0.5;
                if (scrollRef.current.scrollLeft >= scrollRef.current.scrollWidth / 2) {
                    scrollRef.current.scrollLeft = 0;
                }
            }
            requestRef.current = requestAnimationFrame(scroll);
        };
        requestRef.current = requestAnimationFrame(scroll);
        return () => cancelAnimationFrame(requestRef.current);
    }, [isHovered]);

    if (loading) {
        return <Skeleton variant="rectangular" width="100%" height={350} sx={{ borderRadius: 2, mb: 4 }} />;
    }
    if (error) {
        return <Typography color="error" sx={{ mb: 4 }}>{t('tokenExplore.couldNotLoadTrending')}</Typography>;
    }

    return (
        <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('tokenExplore.featuredTokens')}
            </Typography>
            <Box
                ref={scrollRef}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                sx={{ display: 'flex', overflowX: 'hidden', py: 2 }}
            >
                {loopedData.map((collection, index) => (
                    <TrendingCard key={`${ collection.id }-${ index }`} collection={collection} />
                ))}
            </Box>
        </Box>
    );
};

// --- TOOLTIP HELPER COMPONENTS ---
const Stat = ({ label, children, monospace = false }) => (
    <Typography
        component="div"
        variant="body2"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}
    >
        <strong style={{ minWidth: '110px' }}>{label}:</strong>
        <span style={{ fontFamily: monospace ? 'monospace' : 'inherit', wordBreak: 'break-all', opacity: 0.9 }}>
            {children === null || children === undefined ? 'N/A' : children}
        </span>
    </Typography>
);

const BoolStat = ({ label, value }) => (
    <Typography component="div" variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
        <strong>{label}:</strong>
        {value ? (
            <CheckCircleOutline sx={{ color: 'success.main', ml: 1, fontSize: '1rem' }} />
        ) : (
            <HighlightOff sx={{ color: 'error.main', ml: 1, fontSize: '1rem' }} />
        )}
    </Typography>
);

// --- TABLE COMPONENTS ---
const SkeletonRow = ({ columns }) => (
    <TableRow>
        {columns.map((col, index) => (
            <TableCell key={index} align={col.numeric ? 'right' : 'left'}>
                {index === 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Skeleton variant="circular" width={40} height={40} />
                        <Skeleton variant="text" width={100} />
                    </Box>
                ) : (
                    <Skeleton variant="text" width="80%" sx={{ ml: 'auto', display: col.numeric ? 'block' : 'inline-block' }} />
                )}
            </TableCell>
        ))}
    </TableRow>
);

const headCells = [
    { id: 'name', numeric: false, label: 'tokenExplore.token' },
    { id: 'verified', numeric: true, label: 'tokenExplore.verified', noSort: true },
    { id: 'holder_count', numeric: true, label: 'tokenExplore.holders' },
    { id: 'created_time', numeric: true, label: 'tokenExplore.age' },
    { id: 'btc_liquidity', numeric: true, label: 'tokenExplore.liquidity' },
    { id: 'txn_count', numeric: true, label: 'tokenExplore.txs24h' },
    { id: 'price', numeric: true, label: 'tokenExplore.priceSats' },
    { id: 'price_delta_1h', numeric: true, label: 'tokenExplore.change1h' },
    { id: 'price_delta_1d', numeric: true, label: 'tokenExplore.change24h' },
    { id: 'marketcap', numeric: true, label: 'tokenExplore.marketCap' },
];

function EnhancedTableHead({ order, orderBy, onRequestSort }) {
    const createSortHandler = (property) => (event) => onRequestSort(event, property);
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <TableHead>
            <TableRow sx={{ '& .MuiTableCell-root': { borderBottom: `1px solid ${ theme.palette.divider }` } }}>
                {headCells.map((headCell) => {
                    const isActive = orderBy === headCell.id;
                    let SortIcon = UnfoldMore;
                    if (isActive) {
                        // SortIcon = order === 'asc' ? ArrowUpward : ArrowDownward;
                        SortIcon = undefined;
                    }

                    return (
                        <TableCell key={headCell.id} align={headCell.numeric ? 'right' : 'left'} sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
                            {headCell.noSort ? (
                                t(headCell.label)
                            ) : (
                                <TableSortLabel
                                    active={isActive}
                                    direction={isActive ? order : 'asc'}
                                    onClick={createSortHandler(headCell.id)}
                                    IconComponent={SortIcon}
                                    sx={{
                                        '&:hover': { color: theme.palette.text.primary },
                                        '&.Mui-active': { color: theme.palette.primary.main },
                                        '& .MuiTableSortLabel-icon': {
                                            color: 'inherit !important',
                                            opacity: isActive ? 1 : 0.5,
                                        },
                                    }}
                                >
                                    {t(headCell.label)}
                                    {isActive ? (
                                        <Box component="span" sx={visuallyHidden}>
                                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                        </Box>
                                    ) : null}
                                </TableSortLabel>
                            )}
                        </TableCell>
                    );
                })}
            </TableRow>
        </TableHead>
    );
}

// --- MAIN COMPONENT ---
export default function App() {
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(DEFAULT_ITEMS_PER_PAGE);
    const [totalCount, setTotalCount] = useState(0);
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('marketcap');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const totalPages = Math.ceil(totalCount / limit) || 1;
    const navigate = useNavigate();
    const theme = useTheme();
    const { t } = useTranslation();
    const { price: btcPrice } = useBitcoinPrice();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const sortParam = `${ orderBy }:${ order }`;
                const apiUrl = `https://api.odin.fun/v1/tokens?page=${ page }&limit=${ limit }&sort=${ sortParam }`;
                const response = await fetch(apiUrl);
                if (!response.ok) throw new Error(`HTTP error! status: ${ response.status }`);
                const result = await response.json();
                setData(result.data || []);
                setTotalCount(result.count || 0);
            } catch (e) {
                setError(e.message);
                console.error('Failed to fetch token data:', e);
            } finally {
                setTimeout(() => setLoading(false), 500);
            }
        };
        fetchData();
    }, [page, limit, order, orderBy]);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(1);
    };

    const handleChangePage = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
    };

    const renderTooltipContent = (row) => {
        const a = (href, children) => <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>{children}</a>;
        return (
            <Box sx={{ p: 1, width: '550px' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {row.name} ({row.ticker})
                </Typography>
                {row.description && (
                    <Typography variant="body2" sx={{ my: 2 }}>
                        {row.description}
                    </Typography>
                )}
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {t('tokenExplore.generalInfo')}
                        </Typography>
                        <Stat label={t('tokenExplore.id')} monospace>{row.id}</Stat>
                        <Stat label={t('tokenExplore.creator')} monospace>{abbreviateString(row.creator)}</Stat>
                        <Stat label={t('tokenExplore.rune')} monospace>{row.rune}</Stat>
                        <Stat label={t('tokenExplore.created')}>{timeAgo(row.created_time, t)}</Stat>
                        <Stat label={t('tokenExplore.lastAction')}>{timeAgo(row.last_action_time, t)}</Stat>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {t('tokenExplore.supplyDistribution')}
                        </Typography>
                        <Stat label={t('tokenExplore.totalSupply')}>{(row.total_supply / 1e11)?.toLocaleString()}</Stat>
                        <Stat label={t('tokenExplore.sold')}>{`${ (row.sold)?.toLocaleString() || 0 }%`}</Stat>
                        <Stat label={t('tokenExplore.progress')}>{`${ row.progress?.toLocaleString() || 0 }%`}</Stat>
                        <Stat label={t('tokenExplore.decimals')}>{row.decimals}</Stat>
                        <Stat label={t('tokenExplore.divisibility')}>{row.divisibility}</Stat>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1, mb: 1 }}>
                            {t('tokenExplore.marketData')}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '4px 16px' }}>
                            <Stat label={t('tokenExplore.buys24h')}>{row.buy_count?.toLocaleString()}</Stat>
                            <Stat label={t('tokenExplore.sells24h')}>{row.sell_count?.toLocaleString()}</Stat>
                            <Stat label={t('tokenExplore.txs24hTooltip')}>{row.txn_count?.toLocaleString()}</Stat>
                            <Stat label={t('tokenExplore.totalVolume')}>{formatBTCWithUSD(row.volume, btcPrice)}</Stat>
                            <Stat label={t('tokenExplore.swapVolume24h')}>{formatBTCWithUSD(row.swap_volume_24, btcPrice)}</Stat>
                            <Stat label={t('tokenExplore.totalSwapFees')}>{formatBTCWithUSD(row.swap_fees, btcPrice)}</Stat>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1, mb: 1 }}>
                            {t('tokenExplore.statusLinks')}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '4px 16px' }}>
                            <BoolStat label={t('tokenExplore.verified')} value={row.verified} />
                            <BoolStat label={t('tokenExplore.twitterVerified')} value={row.twitter_verified} />
                            <BoolStat label={t('tokenExplore.bonded')} value={row.bonded} />
                            <BoolStat label={t('tokenExplore.featured')} value={row.featured} />
                            <BoolStat label={t('tokenExplore.trading')} value={row.trading} />
                            <BoolStat label={t('tokenExplore.deposits')} value={row.deposits} />
                            <BoolStat label={t('tokenExplore.withdrawals')} value={row.withdrawals} />
                            <BoolStat label={t('tokenExplore.external')} value={row.external} />
                        </Box>
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {row.twitter && <Stat label={t('tokenExplore.twitter')}>{a(`https://twitter.com/${ row.twitter }`, `@${ row.twitter }`)}</Stat>}
                            {row.telegram && <Stat label={t('tokenExplore.telegram')}>{a(row.telegram, t('tokenExplore.viewGroup'))}</Stat>}
                            {row.website && <Stat label={t('tokenExplore.website')}>{a(row.website, row.website)}</Stat>}
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        );
    };

    const renderPriceChange = (currentPrice, pastPrice) => {
        if ([pastPrice, currentPrice].some((p) => p === null || p === undefined) || pastPrice === 0) {
            return { value: 'N/A', color: 'text.secondary' };
        }
        const pctChange = ((currentPrice - pastPrice) / pastPrice) * 100;
        return {
            value: `${ pctChange.toFixed(2) }%`,
            color: pctChange > 0 ? 'success.main' : pctChange < 0 ? 'error.main' : 'grey.500',
        };
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, borderRadius: 4, minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>
            <Container maxWidth={false}>
                <TrendingTokens />
                <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table sx={{ minWidth: 1000 }}>
                            <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                            <TableBody>
                                {loading ? (
                                    Array.from(new Array(limit)).map((_, index) => (
                                        <SkeletonRow key={index} columns={headCells} />
                                    ))
                                ) : error ? (
                                    <TableRow>
                                        <TableCell colSpan={headCells.length} align="center" sx={{ py: 6 }}>
                                            <ErrorOutline color="error" sx={{ mb: 1, fontSize: '2rem' }} />
                                            <Typography color="error">{t('tokenExplore.errorWithStatus', { error })}</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    stableSort(data, getComparator(order, orderBy)).map((row) => {
                                        const change1h = renderPriceChange(row.price, row.price_1h);
                                        const change24h = renderPriceChange(row.price, row.price_1d);
                                        return (
                                            <DetailedTooltip
                                                key={row.id}
                                                title={renderTooltipContent(row)}
                                                placement="bottom-start"
                                                followCursor
                                            >
                                                <TableRow
                                                    hover
                                                    sx={{
                                                        '& .MuiTableCell-root': {
                                                            borderBottom: `1px solid ${ theme.palette.divider }`,
                                                            py: 1.5,
                                                        },
                                                        '&:last-child .MuiTableCell-root': { border: 0 },
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.mode === 'light'
                                                                ? 'rgba(0,0,0,0.04)'
                                                                : 'rgba(255,255,255,0.05)'
                                                        },
                                                    }}
                                                    onClick={() => window.open(`/tokens/${ row.id }`)}
                                                >
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <Avatar
                                                                alt={row.name}
                                                                src={`https://images.odin.fun/token/${ row.id }`}
                                                                sx={{ width: 40, height: 40, border: `1px solid ${ theme.palette.divider }` }}
                                                            />
                                                            <Box>
                                                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                                    {row.ticker || row.name}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        color: 'text.secondary',
                                                                        textTransform: 'uppercase',
                                                                        fontSize: '0.75rem',
                                                                    }}
                                                                >
                                                                    {row.rune}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {row.verified && <VerifiedUser color="success" fontSize="small" />}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                                        {row.holder_count?.toLocaleString() || 'N/A'}
                                                    </TableCell>
                                                    <TableCell align="right">{timeAgo(row.created_time, t)}</TableCell>
                                                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                                        {formatBTCWithUSD(row.btc_liquidity, btcPrice)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                                        {row.txn_count?.toLocaleString() || 'N/A'}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                                        {formatBTCWithUSD(row.price, btcPrice) || 'N/A'}

                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: change1h.color, fontFamily: 'monospace' }}>
                                                        {change1h.value}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: change24h.color, fontFamily: 'monospace' }}>
                                                        {change24h.value}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                                        {formatBTCWithUSD(row.marketcap, btcPrice)}
                                                    </TableCell>
                                                </TableRow>
                                            </DetailedTooltip>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, p: 1, flexWrap: 'wrap' }}>
                    <Button onClick={() => handleChangePage(1)} disabled={page === 1 || loading}>
                        <FirstPage />
                        &nbsp;{t('pagination.first')}
                    </Button>
                    <Button onClick={() => handleChangePage(page - 1)} disabled={page === 1 || loading} startIcon={<KeyboardArrowLeft />}>
                        {t('pagination.prev')}
                    </Button>
                    <Typography sx={{ mx: 2, color: 'text.secondary' }}>
                        {(() => {
                            const translated = t('pagination.pageOf', { current: page, total: totalPages });
                            return translated.includes('{current}') || translated.includes('{total}')
                                ? `Page ${ page } of ${ totalPages }`
                                : translated;
                        })()}
                    </Typography>
                    <Button
                        onClick={() => handleChangePage(page + 1)}
                        disabled={page >= totalPages || loading}
                        endIcon={<KeyboardArrowRight />}
                    >
                        {t('pagination.next')}
                    </Button>
                    <Button onClick={() => handleChangePage(totalPages)} disabled={page >= totalPages || loading}>
                        {t('pagination.last')}&nbsp;
                        <LastPage />
                    </Button>
                </Box>
            </Container>
        </Box>
    );
}
